import { useEffect, useState } from 'react';
import { deriveClientTimezone, tzAbbrFor } from '../lib/clientTimezone';

// v3.15-962: Client local-time badge.
// Reads ONLY client.timezone (with state fallback inside deriveClientTimezone)
// and renders "ABBR • H:MM AM/PM" alongside a green/gray call-window dot.
// Refreshes every 60 seconds — no API calls during render, no geo lookups.
//
// Props:
//   client     — full client object (must include timezone or state)
//   compact    — if true, render a smaller pill style
//   data-testid passthrough via testid prop
export const ClientLocalTimeBadge = ({ client, compact = false, testid }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Refresh every 60 seconds.
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const tz = deriveClientTimezone(client);
  if (!tz) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 ${
          compact ? '' : 'ml-1'
        }`}
        title="Timezone could not be determined from address."
        data-testid={testid || 'client-local-time-unknown'}
      >
        Timezone Unknown
      </span>
    );
  }

  const abbr = tzAbbrFor(tz, now);

  // Format the local time: H:MM AM/PM.
  let timeLabel = '';
  let isInWindow = false;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    timeLabel = fmt.format(now);

    // Determine call window (9 AM - 7 PM local).
    const hourFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      hour12: false,
    });
    const hourPart = hourFmt.formatToParts(now).find((p) => p.type === 'hour');
    const hour = hourPart ? parseInt(hourPart.value, 10) : NaN;
    isInWindow = !Number.isNaN(hour) && hour >= 9 && hour < 19;
  } catch {
    timeLabel = '';
  }

  const dotColor = isInWindow ? 'bg-emerald-500' : 'bg-slate-300';
  const dotTitle = isInWindow
    ? 'Inside recommended call window (9 AM – 7 PM local).'
    : 'Outside recommended call window (9 AM – 7 PM local).';

  return (
    <span
      className={`inline-flex items-center gap-1 ${
        compact
          ? 'text-[10px] font-medium text-slate-600'
          : 'text-[11px] font-medium text-slate-700'
      }`}
      title={`${abbr || ''} • ${timeLabel}\n${dotTitle}`}
      data-testid={testid || 'client-local-time'}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} aria-hidden="true" />
      <span className="text-slate-400">{abbr || ''}</span>
      <span aria-hidden="true" className="text-slate-300">•</span>
      <span>{timeLabel}</span>
    </span>
  );
};

export default ClientLocalTimeBadge;
